import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import type { ReactNode } from 'react';

import {
  COHORT_TAG_IDS_KEY,
  END_DATE_STATUSES_KEY,
  INSIGHTS_STATUSES_KEY,
  MEMBER_STATUSES_KEY,
  MEMBERSHIP_KEY,
  MEMBERSHIP_OPTIONS,
  membershipParamValues,
  membershipSelection,
  SEAT_TYPES_KEY,
  useCompanyMembersParams,
} from '@/features/companyDashboard/common/members/useCompanyMembersParams';
import {
  SESSION_FILTERS,
  useMembersFilterParams,
} from '@/features/companyDashboard/common/members/useMembersFilterParams';
import { useMembersTableState } from '@/features/companyDashboard/common/members/useMembersTableState';
import FilterDropdown from '@/features/membersTable/FilterDropdown';
import MembersTable, {
  emptyMembersTableFacets,
} from '@/features/membersTable/MembersTable';
import {
  COMPANY_ADMIN_MEMBERS_TABLE_COLUMNS,
  COMPANY_ADMIN_MEMBERS_TABLE_FILTERS,
} from '@/features/membersTable/columns';
import { mapMembersTableRow } from '@/features/membersTable/mapMembersTableRow';
import { resolveViewerSessionZone } from '@/features/membersTable/viewerSessionTime';
import { useGetCompanyCohortTagsQuery } from '@/store/api/cohortTagApi';
import {
  useGetMembersTableQuery,
  useUpdateUserMutation,
} from '@/store/api/userApi';

const PAGE_KEY = 'page';

const normalizeSearch = (value: string) => value.trim();

interface PartnerMembersProps {
  headerActions?: ReactNode;
}

const PartnerMembers = ({ headerActions }: PartnerMembersProps) => {
  const {
    handleLimitChange,
    handleSort,
    limit,
    page,
    search,
    setSearch,
    sortDirection,
    sortField,
    validLimits,
  } = useMembersTableState();
  const [, setSearchParams] = useSearchParams();
  const { arrayFilter, sessionFilter, setArrayFilter, setSessionFilter } =
    useMembersFilterParams();
  const membersParams = useCompanyMembersParams('partner');
  const { companyId = '' } = useParams();
  const [updateUser] = useUpdateUserMutation();
  const [searchQuery, setSearchQuery] = useState(search);
  const lastCommittedSearchRef = useRef(search);
  const isClearingSearchRef = useRef(false);

  const membershipFilter = membershipSelection(arrayFilter(MEMBERSHIP_KEY));
  const memberStatusFilter = membersParams.memberStatuses ?? [];
  const endDateStatusFilter = membersParams.endDateStatuses ?? [];
  const insightsStatusFilter = membersParams.insightsStatuses ?? [];
  const seatTypeFilter = membersParams.seatTypes ?? [];
  const cohortFilter = membersParams.cohortTagIds ?? [];
  const { data: cohortTags = [] } = useGetCompanyCohortTagsQuery(companyId, {
    skip: !companyId,
  });

  useEffect(() => {
    if (isClearingSearchRef.current) {
      if (search) return;

      isClearingSearchRef.current = false;
      lastCommittedSearchRef.current = '';
    }

    const normalizedSearchQuery = normalizeSearch(searchQuery);
    if (search === normalizedSearchQuery) {
      lastCommittedSearchRef.current = search;
      return;
    }

    const isStaleDebouncedEcho =
      search === lastCommittedSearchRef.current &&
      normalizedSearchQuery !== lastCommittedSearchRef.current;
    if (isStaleDebouncedEcho) return;

    lastCommittedSearchRef.current = search;
    setSearchQuery(search);
  }, [search, searchQuery]);

  useEffect(() => {
    if (isClearingSearchRef.current) return undefined;

    const normalizedSearchQuery = normalizeSearch(searchQuery);
    if (normalizedSearchQuery === search) return undefined;

    const timeoutId = window.setTimeout(() => {
      lastCommittedSearchRef.current = normalizedSearchQuery;
      setSearch(searchQuery);
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [search, searchQuery, setSearch]);

  const {
    data: queryData,
    isError,
    isFetching,
    isLoading,
  } = useGetMembersTableQuery(
    {
      ...membersParams,
      limit,
      page,
      sortBy: sortField,
      sortDirection,
    },
    { skip: !companyId || !membersParams.companyMemberStatus },
  );
  // A skipped query keeps its last result; with no Membership selected, show nothing.
  const data = membersParams.companyMemberStatus ? queryData : undefined;

  const viewerZone = resolveViewerSessionZone();
  const zoneKey = viewerZone.status === 'ready' ? viewerZone.timeZone : null;
  const rows = useMemo(
    () => data?.items.map((item) => mapMembersTableRow(item, viewerZone)) ?? [],
    [data?.items, zoneKey],
  );

  const total = data?.total ?? 0;

  return (
    <div className="mr-4 flex flex-1 flex-col pb-4">
      <MembersTable
        businessPlanFilter={[]}
        coachesAssignedFilter={[]}
        coachFilter={[]}
        cohortFilter={cohortFilter}
        cohortOptions={cohortTags}
        columns={COMPANY_ADMIN_MEMBERS_TABLE_COLUMNS}
        companyFilter={[]}
        enableRowNavigation={false}
        endDateStatusFilter={endDateStatusFilter}
        error={isError}
        facets={{
          ...emptyMembersTableFacets,
          ...data?.facets,
        }}
        filters={COMPANY_ADMIN_MEMBERS_TABLE_FILTERS}
        growthPlanStartedFilter={[]}
        headerActions={headerActions}
        insightsStatusFilter={insightsStatusFilter}
        isFetching={isFetching}
        leadingFilters={
          <FilterDropdown
            label="Membership"
            options={MEMBERSHIP_OPTIONS}
            selected={membershipFilter}
            onChange={(selected) =>
              setArrayFilter(MEMBERSHIP_KEY, membershipParamValues(selected))
            }
          />
        }
        loading={isLoading}
        memberStatusFilter={memberStatusFilter}
        page={page - 1}
        pageSize={limit}
        pageSizeOptions={validLimits}
        rows={rows}
        searchQuery={searchQuery}
        seatTypeFilter={seatTypeFilter}
        sessionFilter={sessionFilter}
        showCoachFilter={false}
        sortDirection={sortDirection}
        sortField={sortField}
        title={isLoading ? 'Members' : `Members (${total})`}
        titleVariant="h4"
        total={total}
        totalPages={data?.pages ?? 0}
        stickyHeader
        onAdminNoteSave={async (userId, adminNote) => {
          await updateUser({ id: userId, user: { adminNote } }).unwrap();
        }}
        onBusinessPlanFilterChange={() => undefined}
        onClearAllFilters={() => {
          isClearingSearchRef.current = true;
          lastCommittedSearchRef.current = '';
          setSearchQuery('');
          setSearchParams((currentParams) => {
            const nextParams = new URLSearchParams(currentParams);
            nextParams.delete('search');
            nextParams.delete(MEMBER_STATUSES_KEY);
            nextParams.delete(END_DATE_STATUSES_KEY);
            nextParams.delete(INSIGHTS_STATUSES_KEY);
            nextParams.delete(SEAT_TYPES_KEY);
            nextParams.delete(COHORT_TAG_IDS_KEY);
            SESSION_FILTERS.forEach((filter) => nextParams.delete(filter));
            nextParams.set(PAGE_KEY, '1');
            return nextParams;
          });
        }}
        onCoachesAssignedFilterChange={() => undefined}
        onCoachFilterChange={() => undefined}
        onCohortFilterChange={(selected) =>
          setArrayFilter(COHORT_TAG_IDS_KEY, selected)
        }
        onCompanyFilterChange={() => undefined}
        onEndDateStatusFilterChange={(selected) =>
          setArrayFilter(END_DATE_STATUSES_KEY, selected)
        }
        onGrowthPlanStartedFilterChange={() => undefined}
        onInsightsStatusFilterChange={(selected) =>
          setArrayFilter(INSIGHTS_STATUSES_KEY, selected)
        }
        onMemberStatusFilterChange={(selected) =>
          setArrayFilter(MEMBER_STATUSES_KEY, selected)
        }
        onPageChange={(nextPage) => {
          setSearchParams((currentParams) => {
            const nextParams = new URLSearchParams(currentParams);
            nextParams.set(PAGE_KEY, `${nextPage + 1}`);
            return nextParams;
          });
        }}
        onPageSizeChange={(pageSize) => {
          handleLimitChange({
            target: { name: 'limit', value: pageSize },
          } as Parameters<typeof handleLimitChange>[0]);
        }}
        onSearchChange={setSearchQuery}
        onSeatTypeFilterChange={(selected) =>
          setArrayFilter(SEAT_TYPES_KEY, selected)
        }
        onSessionFilterChange={setSessionFilter}
        onSortChange={handleSort}
      />
    </div>
  );
};

export default PartnerMembers;
